import React, { useCallback, useMemo } from 'react';

import { Box, Button, CircularProgress } from '@mui/material';

import { useVulnerabilitiesAggregation } from 'queries/search/use-vulnerabilities-aggregation';

import useStyles from './styles';

const ALL = 'All';

interface Props {
    selected?: number;
    onSelect: (year: number | null) => void;
    getOptionHref: (year: number | null) => string;
    className?: string;
}

const YearFilter: React.FC<Props> = ({ selected, onSelect, getOptionHref, className }) => {
    const { classes, cx } = useStyles();
    const { data: aggregation, isLoading } = useVulnerabilitiesAggregation(
        { termName: 'type', termValue: 'cve' },
        { select: ({ bulletingAggregation }) => bulletingAggregation }
    );

    const handleSelect = useCallback(
        (event) => {
            event.preventDefault();
            const { option } = event.currentTarget.dataset;
            const value = option?.toLowerCase() ?? null;
            onSelect && onSelect(value);
        },
        [onSelect]
    );

    const years = useMemo(() => {
        const output = new Set<number>();

        if (aggregation) {
            Object.values(aggregation).forEach((stats) =>
                Object.keys(stats).forEach((year) => output.add(parseInt(year)))
            );
        }

        return [...output].sort((yearA, yearB) => yearB - yearA);
    }, [aggregation]);

    const options = useMemo(
        () => [{ label: ALL, value: null }, ...years.map((year) => ({ label: year, value: year }))],
        [years]
    );

    return (
        <Box className={cx(classes.container, className)}>
            {isLoading ? (
                <Box className={classes.spinner}>
                    <CircularProgress />
                </Box>
            ) : (
                options.map((option) => {
                    const additional = getOptionHref ? { component: 'a', href: getOptionHref(option.value) } : {};
                    return (
                        <Button
                            key={option.label}
                            className={cx(classes.button, { [classes._selected]: option.value === (selected || null) })}
                            variant="outlined"
                            data-option={option.value}
                            onClick={handleSelect}
                            {...additional}
                        >
                            {option.label}
                        </Button>
                    );
                })
            )}
        </Box>
    );
};

export default YearFilter;
